import { useState, useContext, useEffect } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import MainSectionSection from "../../components/MainSectionSection";
import LeadPriceQualificationRuleCard from "../../components/LeadPriceQualificationRuleCard";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";

type PriceQualifyRule = {
  name: string;
  priceMin: number | undefined;
  priceMax: number | undefined;
  alwaysTransfer: boolean;
};

const LeadQualificationContainer = () => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useContext();

  const [priceRules, setPriceRules] = useState<PriceQualifyRule[]>([]);

  const { data: merchant } = trpc.merchants.get.useQuery(
    { id: merchantId },
    { enabled: !!merchantId }
  );

  const updatePriceRulesMutation = trpc.merchants.updateLeadPriceQualifyRules.useMutation({
    onSuccess: () => {
      toast.success("Reglas de precio actualizadas", {
        description: "Las reglas de calificación por precio han sido actualizadas exitosamente",
      });
      utils.merchants.get.invalidate({ id: merchantId });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Hubo un error al actualizar las reglas de precio",
      });
    },
  });

  useEffect(() => {
    if (merchant?.leadPriceQualifyRules) {
      setPriceRules(
        merchant.leadPriceQualifyRules.map((rule) => ({
          name: rule.name,
          priceMin: rule.priceMin,
          priceMax: rule.priceMax,
          alwaysTransfer: rule.alwaysTransfer ?? false,
        }))
      );
    }
  }, [merchant?.leadPriceQualifyRules]);

  const handleAddPriceRule = () => {
    setPriceRules([
      ...priceRules,
      {
        name: "",
        priceMin: undefined,
        priceMax: undefined,
        alwaysTransfer: false,
      },
    ]);
  };

  const handleRemovePriceRule = (index: number) => {
    setPriceRules(priceRules.filter((_, i) => i !== index));
  };

  const handlePriceRuleChange = (
    index: number,
    field: keyof PriceQualifyRule,
    value: string | number | boolean | undefined
  ) => {
    const newRules = [...priceRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setPriceRules(newRules);
  };

  const handleSavePriceRules = () => {
    const hasEmptyNames = priceRules.some((rule) => !rule.name.trim());
    if (hasEmptyNames) {
      toast.error("Error", {
        description: "Por favor ingrese un nombre para cada regla de precio",
      });
      return;
    }

    updatePriceRulesMutation.mutate({
      merchantId,
      leadPriceQualifyRules: priceRules.map((rule) => ({
        name: rule.name,
        priceMin: rule.priceMin,
        priceMax: rule.priceMax,
        alwaysTransfer: rule.alwaysTransfer,
      })),
    });
  };

  return (
    <MainSectionSection gap="24px">
      <div>
        <div className="rounded-md bg-white p-4 shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)] md:p-6">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">
                Reglas de Calificación por Precio
              </h2>
            </div>

            <Alert status="info">
              <div>
                <p className="font-semibold">¿Cómo funcionan las reglas de precio?</p>
                <p className="text-sm">
                  Cuando un lead busca propiedades o consulta un inmueble, el sistema evalúa su
                  presupuesto contra estas reglas. Marca "Siempre transferir" en las reglas que
                  deban enviar el lead a un asesor humano de inmediato cuando coincidan.
                </p>
              </div>
            </Alert>

            <div className="flex flex-col gap-4">
              {priceRules.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-gray-500">
                    No hay reglas de precio configuradas. Haga clic en "Agregar Regla de Precio"
                    para comenzar.
                  </p>
                </div>
              ) : (
                priceRules.map((rule, index) => (
                  <LeadPriceQualificationRuleCard
                    key={index}
                    rule={rule}
                    onRuleChange={(field, value) => handlePriceRuleChange(index, field, value)}
                    onRemove={() => handleRemovePriceRule(index)}
                  />
                ))
              )}
            </div>

            <div className="flex flex-col justify-between gap-3 md:flex-row">
              <Button
                variant="outline"
                onClick={handleAddPriceRule}
                className="min-h-11 w-full md:w-auto"
              >
                <Plus className="mr-2 size-4" />
                Agregar Regla de Precio
              </Button>
              <Button
                onClick={handleSavePriceRules}
                disabled={updatePriceRulesMutation.isLoading}
                className="min-h-11 w-full md:w-auto"
              >
                {updatePriceRulesMutation.isLoading ? "Guardando..." : "Guardar Reglas de Precio"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainSectionSection>
  );
};

export default LeadQualificationContainer;
